/**
 * 공지사항 팝업 모달
 * 로그인 시 읽지 않은 팝업 공지를 표시
 * 화면 중앙에 위치하며 휴대폰 크기에 맞게 자동 조정
 */
import React, { useState } from 'react';
import { NoticeResult } from '../types/notice.types';
import styles from '../styles/NoticePopupModal.module.css';

interface NoticePopupModalProps {
    notice: NoticeResult;
    zIndex: number;
    onClose: () => void;
    onHideForToday: (noticeId: number) => void;
}

/**
 * 날짜 포맷팅 함수
 */
const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    try {
        // 배열 형태의 날짜 처리 [년, 월, 일, 시, 분, 초]
        if (Array.isArray(dateStr)) {
            const [year, month, day, hour = 0, minute = 0] = dateStr as unknown as number[];
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        }
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '-';
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    } catch {
        return '-';
    }
};

const NoticePopupModal: React.FC<NoticePopupModalProps> = ({
    notice,
    zIndex,
    onClose,
    onHideForToday
}) => {
    const [hideForToday, setHideForToday] = useState(false);
    const isUrgent = notice.urgentYn === 'Y';

    const handleConfirm = () => {
        if (hideForToday) {
            onHideForToday(notice.noticeId);
        }
        onClose();
    };

    return (
        <div className={styles['notice-popup-overlay']} style={{ zIndex }}>
            <div className={`${styles['notice-popup-modal']} ${isUrgent ? styles['notice-popup-urgent'] : ''}`}>
                <div className={styles['notice-popup-header']}>
                    <span className={`${styles['notice-popup-badge']} ${isUrgent ? styles['notice-popup-badge-urgent'] : ''}`}>
                        {isUrgent ? '긴급 공지' : '공지'}
                    </span>
                    <button
                        className={styles['notice-popup-close']}
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className={styles['notice-popup-body']}>
                    <h3 className={styles['notice-popup-title']}>{notice.title}</h3>
                    <div className={styles['notice-popup-meta']}>
                        <span className={styles['notice-popup-date']}>
                            {formatDate(notice.startDate)}
                        </span>
                        <span className={styles['notice-popup-writer']}>
                            {notice.writerTypeName || '본사'}
                        </span>
                    </div>
                    <div className={styles['notice-popup-content']}>
                        {notice.content.split('\n').map((line, idx) => (
                            <p key={idx}>{line || '\u00A0'}</p>
                        ))}
                    </div>
                </div>

                <div className={styles['notice-popup-footer']}>
                    <label className={styles['notice-popup-checkbox-wrapper']}>
                        <input
                            type="checkbox"
                            className={styles['notice-popup-checkbox']}
                            checked={hideForToday}
                            onChange={(e) => setHideForToday(e.target.checked)}
                        />
                        <span className={styles['notice-popup-checkbox-label']}>오늘 하루 보지 않기</span>
                    </label>
                    <div className={styles['notice-popup-buttons']}>
                        <button
                            className={styles['notice-popup-btn-confirm']}
                            onClick={handleConfirm}
                        >
                            확인
                        </button>
                    </div>
                </div>

                <div className={styles['notice-popup-logo-area']}>
                    <img
                        src="/vroong-logo.svg"
                        alt="부릉라이더스"
                        className={styles['notice-popup-logo']}
                    />
                </div>
            </div>
        </div>
    );
};

export default NoticePopupModal;
